import type { Connection, ConnectionStatus } from '../services/admin-api';

/** Estado visible; `connecting` solo existe en la UI mientras se prueba. */
export type VisibleStatus = ConnectionStatus | 'connecting';

export const STATUS_LABEL: Readonly<
  Record<
    VisibleStatus,
    { label: string; variant: 'outline' | 'success' | 'warning' | 'destructive' | 'secondary' }
  >
> = {
  pending: { label: 'Pendiente', variant: 'outline' },
  connecting: { label: 'Conectando...', variant: 'secondary' },
  connected: { label: 'Conectado', variant: 'success' },
  error: { label: 'Error', variant: 'destructive' },
  unavailable: { label: 'No disponible', variant: 'warning' },
};

export const SSL_MODES = [
  { value: 'prefer', label: 'Preferir cifrado (recomendado)' },
  { value: 'require', label: 'Exigir cifrado' },
  { value: 'verify-full', label: 'Exigir cifrado y verificar el certificado' },
  { value: 'verify-ca', label: 'Exigir cifrado y verificar la autoridad' },
  { value: 'disable', label: 'Sin cifrado (solo redes de confianza)' },
] as const;

export type SslMode = (typeof SSL_MODES)[number]['value'];

export const MYSQL_SSL_MODES = [
  { value: 'verify-full', label: 'Exigir cifrado y verificar el certificado (recomendado)' },
  { value: 'require', label: 'Exigir cifrado sin verificar el certificado' },
  { value: 'disable', label: 'Sin cifrado (solo redes de confianza)' },
] as const;

export function formatBytes(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${String(bytes)} B`;
}

export function formatDate(iso: string | null): string {
  if (iso === null) return 'Nunca';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? 'Desconocida'
    : date.toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' });
}

/** Descripcion breve del origen, sin rutas ni secretos (el servidor tampoco los envia). */
export function sourceSummary(connection: Connection): string {
  if (connection.connectorId === 'postgresql' || connection.connectorId === 'mysql') {
    const port = connection.port === undefined ? '' : `:${String(connection.port)}`;
    return `${connection.user ?? ''}@${connection.host ?? ''}${port}/${connection.database ?? ''}`;
  }
  if (connection.connectorId === 'sqlserver') {
    const scope = [connection.database, connection.schema].filter(Boolean).join('.');
    const who = connection.authType === 'entra-sp' ? 'service principal' : (connection.user ?? '');
    return `${who}@${connection.host ?? ''} · ${scope}`;
  }
  if (connection.connectorId === 'databricks') {
    const scope = [connection.catalog, connection.schema].filter(Boolean).join('.');
    return scope === '' ? (connection.host ?? '') : `${connection.host ?? ''} · ${scope}`;
  }
  return connection.fileName ?? 'Archivo';
}
